<script setup>
import dayjs from 'dayjs'
import { reset } from '@formkit/core'
import { useReCaptcha } from 'vue-recaptcha-v3'

const props = defineProps({
  inputClass: {
    type: String,
    default: '',
  },
  formClass: {
    type: String,
    default: '',
  },
  buttonClass: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
})

const { t } = useI18n()
const alertActive = ref(false)
const alertError = ref(false)
const error = reactive({
  description: '',
})

const form = reactive({
  header: 'subscription',
  title: 'Please review the details of your subscription below.',
  email: '',
  subject: 'Thank you for your interest – Dexon Technology PLC',
  body: '',
})

function validateEmail() {
  const re = /\S+@\S+\.\S+/
  return re.test(form.email)
}

const { postSubscribe } = useSubscribe()

const getCurrentDate = ref('')
const getAllData = ref(null)
const disabled = ref(false)

const { executeRecaptchaTokens } = useGoogleRecaptcha()
const captchaVerified = ref(false)

async function sendEmail() {
  disabled.value = true
  const [token, emailToken, saveToken] = await executeRecaptchaTokens('submit', 3)

  if (form.email !== '' && validateEmail()) {
    const database = await postSubscribe(form.email, saveToken)
    const date = new Date()
    getCurrentDate.value = dayjs(date).format('DD MMMM YYYY')
    getAllData.value = database.data.value?.number

    if (database.status.value === 'success') {
      const verifyRecaptcha = useFetch('/api/verify-recaptcha', {
        method: 'POST',
        body: JSON.stringify({ token }),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const subscribeRequest = useFetch('api/subscribe', {
        method: 'POST',
        body: {
          header: form.header,
          title: form.title,
          email: form.email,
          subject: form.subject,
          body: form.body,
        },
      })

      const userSubscribeRequest = useFetch('/api/user/subscribe', {
        method: 'POST',
        body: {
          token: emailToken,
          date: getCurrentDate.value,
          number: getAllData.value,
          header: form.header,
          title: form.title,
          email: form.email,
          subject: form.subject,
          body: form.body,
        },
      })

      const [recaptchaResponse, subscribeResponse, userSubscribeResponse] = await Promise.all([
        verifyRecaptcha,
        subscribeRequest,
        userSubscribeRequest,
      ])

      disabled.value = false

      if (recaptchaResponse.status.value === 'success' && subscribeResponse.status.value === 'success' && userSubscribeResponse.status.value === 'success') {
        reset('myForm')
        alertActive.value = true
        captchaVerified.value = true
      }
    }

    else {
      error.description = 'This email is already subscribed'
      alertError.value = true
      captchaVerified.value = false
    }
  }
  else {
    error.description = $t('alert.error.description')
    alertError.value = true
    captchaVerified.value = false
  }
}
</script>

<template>
  <div class="relative w-full" :class="{ 'flex justify-center': props.formClass }">
    <FormKit
      id="myForm"
      v-slot="{ disabled }"
      type="form"
      :actions="false"
      :submit-label="t('button.submit')"
      :form-class="`${props.formClass ? props.formClass : ''} flex items-center mr-auto relative sm:max-w-md max-w-full w-full form-subscribe`"
      @submit="sendEmail"
    >
      <FormKit
        v-model="form.header"
        type="text"
        label="Header"
        name="header"
        :classes="{
          outer: 'hidden',
        }"
      />
      <FormKit
        v-model="form.title"
        type="text"
        label="Title"
        name="title"
        :classes="{
          outer: 'hidden',
        }"
      />
      <FormKit
        v-model="form.subject"
        type="text"
        label="Subject"
        name="subject"
        help="Subject of your email."
        :classes="{
          outer: 'hidden',
        }"
      />
      <FormKit
        v-model="form.email"
        type="email"
        name="email"
        validation="email"
        validation-visibility="live"
        :label="false"
        :input-class="{
          'formkit-input': false,
        }"
        :placeholder="$t('footer.follow.email')"
        :classes="{
          message: 'text-red-500 text-sm inset-0',
          wrapper: false,
          inner: '!border-none !shadow-none',
          outer: '!mb-0 w-full relative',
          input: 'w-[calc(100%-75px)] !bg-white text-sm pl-4 pr-10 py-3.5 text-md relative z-0 shadow-shadow-light/10 shadow-custom-lg rounded-lt-full rounded-lb-full  text-secondary-gray-500 font-medium placeholder:text-secondary-gray-500 placeholder:font-medium',
        }"
      />
      <FormKit
        type="submit"
        label="Submit"
        :disabled="disabled"
        :input-class="{
          'formkit-input': false,
        }"
        :classes="{
          outer: '!mb-0',
          input: 'bg-red-gradient absolute inset-y-0 right-0 z-1 m-auto items-center whitespace-nowrap rounded-full px-6 py-3 text-sm font-semibold text-white ring-secondary-red-100/25 transition duration-200 ease-out hover:ring-4',
        }"
      />
      <p v-if="captchaVerified" class="mt-3 text-sm text-white">
        <span>reCAPTCHA verified!</span>
      </p>
    </FormKit>
  </div>
  <UIAlert v-model="alertActive" theme="success" :title="t('alert.success.title')" :description="t('alert.success.description')" />
  <UIAlert v-model="alertError" theme="error" :title="t('alert.error.title')" :description="error.description" />
</template>

<style>
.form-subscribe ul.formkit-messages {
  @apply absolute bottom-0 translate-y-[85%];
}
</style>
